import { useState, useEffect } from 'react';

import { z } from 'zod';

import { nonFalsey } from '@/utils/nonFalsey';

import { useFileSearch } from '@/modules/files/hooks/useFileSearch';

import type { FileSearchResult } from './LinkInput';
import { LinkInput } from './LinkInput';

/**
 * This component has changed substantially given MUI's Autocomplete does most of what this did natively. Props have changed
 *
 * onChange: (value: string | SearchResult) => void is split into ->
 *  onInputChange (value: string) => void
 *  onChange (value: FileSearchResult) => void
 * invalid -> error
 */
interface Props {
  ownerIds: string[];
  error?: boolean;
  value: string;
  onChange: (value: FileSearchResult) => void;
  onInputChange: (value: string) => void;
}

export const LinkInputWithSearcher = ({
  ownerIds,
  error,
  value,
  onChange,
  onInputChange,
}: Props) => {
  const [textToSearch, setTextToSearch] = useState('');
  const [inputValue, setInputValue] = useState(value);
  const [showResults, setShowResults] = useState(true);

  const { data, loading } = useFileSearch(ownerIds, textToSearch);

  const searchResults = data?.files?.filter(nonFalsey) ?? [];

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleSearchTextChange = (textToSearch: string) => {
    setTextToSearch(textToSearch);
    setShowResults(true);
  };

  return (
    <LinkInput
      loading={loading}
      options={showResults && inputValue ? searchResults : []}
      inputValue={inputValue}
      onChange={onChange}
      onInputChange={(inputValue) => {
        setInputValue(inputValue);

        if (z.string().url().safeParse(inputValue).success) {
          onInputChange(inputValue);
          setShowResults(false);
        } else {
          handleSearchTextChange(inputValue);
        }
      }}
      error={error}
    />
  );
};
